import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Count } from "./badge";

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("rounded-2xl border border-line/70 bg-surface shadow-card", className)}
      {...props}
    />
  );
}

/** Dark rounded square holding a white icon, used in card and KPI headers. */
export function IconTile({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex size-9 shrink-0 items-center justify-center rounded-lg bg-dark text-white [&_svg]:size-[18px]",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function CardHeader({
  icon,
  title,
  count,
  description,
  action,
}: {
  icon?: ReactNode;
  title: ReactNode;
  count?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 px-5 pt-5 pb-3">
      {icon && <IconTile>{icon}</IconTile>}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          {count !== undefined && <Count>{count}</Count>}
        </div>
        {description && <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("px-5 pb-5", className)} {...props} />;
}
